// @flow

import type { Endpoints, EntityFieldCollection, SortParams } from '../types';
import {
    createdAtField,
    getCreatedAtSort,
    idField,
    payfactoMerchantNoField,
    paymentModeField,
} from '../abstract/StoredEntity';
import type { StoredEntity } from '../abstract/StoredEntity';
import { constructEndpoints } from '../helpers';
import Organization from './Organization';
import Beacon from './Beacon';
import OpeningHours from './OpeningHours';
import LocalizedString from './LocalizedString';

class OrgLocation implements StoredEntity {
    static classInternalName = 'org_location';

    static fields: EntityFieldCollection = {
        id: idField,
        createdAt: createdAtField,
        name: {
            displayName: 'Name',
            recordName: 'name',
            dataType: {
                primitive: 'simple-entity',
                isArray: true,
                extraData: {
                    model: () => LocalizedString,
                },
            },
            supportsQuery: false,
            supportsSort: false,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        address: {
            displayName: 'Address',
            recordName: 'address',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: false,
        },
        city: {
            displayName: 'City',
            recordName: 'city',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: false,
        },
        subdivision: {
            displayName: 'Province / State',
            recordName: 'subdivision',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: false,
        },
        country: {
            displayName: 'Country',
            recordName: 'country',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: false,
        },
        postalCode: {
            displayName: 'Postal Code',
            recordName: 'postal_code',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: false,
        },
        phone: {
            displayName: 'Phone',
            recordName: 'phone',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        description: {
            displayName: 'Description',
            recordName: 'description',
            dataType: {
                primitive: 'simple-entity',
                isArray: true,
                extraData: {
                    model: () => LocalizedString,
                },
            },
            supportsQuery: false,
            supportsSort: false,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        latitude: {
            displayName: 'Latitude',
            recordName: 'latitude',
            dataType: {
                primitive: 'num',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        longitude: {
            displayName: 'Longitude',
            recordName: 'longitude',
            dataType: {
                primitive: 'num',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        organizationId: {
            displayName: 'Organization',
            recordName: 'organization_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => Organization,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        posModel: {
            displayName: 'POS Model',
            recordName: 'pos_model',
            dataType: {
                primitive: 'enum',
                isArray: false,
                extraData: {
                    1: 'Generic',
                    2: 'Veloce',
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        posToken: {
            displayName: 'POS Token',
            recordName: 'pos_token',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: false,
            canEdit: false,
            isOptional: false,
            displayOnMain: true,
        },
        defaultBeaconMinor: {
            displayName: 'Default Beacon Minor',
            recordName: 'default_beacon_minor',
            dataType: {
                primitive: 'num',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: false,
            canEdit: false,
            isOptional: false,
            displayOnMain: true,
        },
        beaconId: {
            displayName: 'Beacon',
            recordName: 'beacon_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => Beacon,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: false,
            canEdit: true,
            isOptional: true,
            displayOnMain: true,
        },
        enabled: {
            displayName: 'Enabled',
            recordName: 'enabled',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        posUrl: {
            displayName: 'POS URL',
            recordName: 'pos_url',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: true,
            displayOnMain: false,
        },
        openingHours: {
            displayName: 'Opening Hours',
            recordName: 'opening_hours',
            dataType: {
                primitive: 'simple-entity',
                isArray: true,
                extraData: {
                    model: () => OpeningHours,
                },
            },
            supportsQuery: false,
            supportsSort: false,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: false,
        },
        paymentMode: { ...paymentModeField, canEdit: true, needToCreate: true },
        payfactoMerchantNo: { ...payfactoMerchantNoField, canEdit: true, needToCreate: true },
        paymentProvider: {
            displayName: 'Payment Provider',
            recordName: 'payment_provider',
            dataType: {
                primitive: 'enum',
                isArray: false,
                extraData: {
                    payfacto: 'PayFacto (default)',
                    stripe: 'Stripe',
                    mock: 'Mock',
                    none: 'None',
                },
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: true,
            isOptional: true,
            displayOnMain: true,
        },
        stripeAccountId: {
            displayName: 'Stripe Account ID (optional)',
            recordName: 'stripe_account_id',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: false,
            canEdit: true,
            isOptional: true,
            displayOnMain: false,
        },
    };

    static fileFields: EntityFieldCollection = {
        logo: {
            displayName: 'Logo',
            fileType: 'img',
            fileName: `logo`,
            urlName: 'logo',
            isArray: false,
            displayOnMain: true,
        },
        cover: {
            displayName: 'Cover',
            fileType: 'img',
            fileName: `cover`,
            urlName: 'cover',
            isArray: false,
            displayOnMain: false,
        },
        menu: {
            displayName: 'Menu',
            fileType: 'img',
            fileName: `menu`,
            urlName: 'menu',
            isArray: false,
            displayOnMain: false,
        },
        featurePhotos: {
            displayName: 'Feature Photos',
            fileType: 'img',
            fileName: `feature_photos`,
            urlName: `feature-photos`,
            isArray: true,
            displayOnMain: false,
        },
    };

    static defaultSort: SortParams = getCreatedAtSort('created_at');

    get id() {
        return this.record[OrgLocation.fields.id.recordName];
    }

    get displayName() {
        let name = this.id;
        const defaultLoc = this.record[OrgLocation.fields.name.recordName].filter(
            (loc) => loc[LocalizedString.fields.languageCode.recordName] === 'en',
        );
        if (defaultLoc.length !== 0) name = defaultLoc[0].text;
        return name;
    }

    static endpoints: Endpoints = constructEndpoints('organizations/locations');

    constructor(record: any) {
        this.record = record;
    }
}

export default OrgLocation;
